<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounce } from '@vueuse/core';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import ImageResize from 'quill-resize-image';
import { isRichTextEmpty, looksLikeHtml, normalizeHtmlForQuill } from '@Helpers';

Quill.register('modules/ImageResize', ImageResize);

defineOptions({ inheritAttrs: false });

interface NoteTemplateOption {
  _id?: string;
  title: string;
  body?: string;
}

const emits = defineEmits<{
  'update:modelValue': [string];
}>();

const props = defineProps<{
  error?: string;
  editorHeight?: string;
  field?: any;
  modelValue?: string;
  placeholder?: string;
}>();

const UPLOAD_MIMETYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp'];

const DEFAULT_TOOLBAR_CONTAINER = [
  ['bold', 'italic', 'underline', 'link', 'image', { list: 'bullet' }, { list: 'ordered' }],
];

const RICH_TOOLBAR_CONTAINER = [
  [{ size: [] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['link', 'image'],
  ['clean'],
];

let quill: Quill;
// True while applyHtmlToEditor() feeds stored HTML through the clipboard: the base64 guard skips
// those — it stops NEW pasted base64, and must never rewrite content that is already persisted.
let isApplyingStoredHtml = false;

const _model = ref(props.modelValue || '');
const pendingNoteTemplate = ref<NoteTemplateOption | null>(null);
const quillEditor = ref<HTMLElement | null>(null);
const replaceTemplateConfirmVisible = ref(false);
const templateMenu = ref();
const uploadCount = ref(0);
const uploading = ref(false);

const input = useDebounce(_model, 400);

const canUseNoteTemplates = computed(() => !!props.field?.settings?.allowNoteTemplates);
const editorBorder = computed(() => (props.error ? '1px solid red' : '1px solid #ccc'));
const hideImage = computed(() => props.field?.settings?.hideImage === true);
const isBatchUpload = computed(() => uploadCount.value > 1);
const templateMenuDomId = computed(() => `exam_note_template_menu_${props.field?.name || 'field'}`);
const uploadImage = computed<((file: File) => Promise<string>) | undefined>(() => props.field?.settings?.uploadImage);

const editorHeight = computed(() => {
  if (props.editorHeight) return props.editorHeight;

  return canUseNoteTemplates.value ? '160px' : '100px';
});
const noteTemplates = computed((): NoteTemplateOption[] => {
  const templates = props.field?.settings?.noteTemplates;

  return Array.isArray(templates) ? templates : [];
});
// Nothing to pick from is the same as no picker: the shelter has no active templates, or the fetch
// has not landed yet (`noteTemplates` starts empty). Showing a button that opens an empty menu is
// worse than showing no button.
const canPickNoteTemplate = computed(() => {
  const isFieldDisabled = !!props.field?.state?.disabled;

  return canUseNoteTemplates.value && !isFieldDisabled && !!noteTemplates.value.length;
});
const templateMenuItems = computed(() =>
  noteTemplates.value.map((template) => ({
    label: template.title,
    command: () => onSelectNoteTemplate(template),
  }))
);
const toolbarOverride = computed(() => {
  const toolbar = props.field?.settings?.toolbar;

  return Array.isArray(toolbar) ? toolbar : null;
});

onMounted(initQuill);

watch(input, onModelDebounce);
watch(() => props.modelValue, onExternalModelValue);

// HELPERS
// Drop the image control from whichever toolbar applies, for fields that ask for a text-only editor.
// Returns a copy: the toolbar containers are module-level constants shared by every editor instance,
// so filtering one in place would silently remove the image button everywhere else too.
function withoutImageControl(container: any[]) {
  return container
    .map((group: any[]) => group.filter((control: any) => control !== 'image'))
    .filter((group: any[]) => group.length);
}

function buildToolbar() {
  const selectedContainer =
    toolbarOverride.value || (canUseNoteTemplates.value ? RICH_TOOLBAR_CONTAINER : DEFAULT_TOOLBAR_CONTAINER);
  const container = hideImage.value ? withoutImageControl(selectedContainer) : selectedContainer;

  if (!uploadImage.value) return container;

  return { container, handlers: { image: onImageButtonClick } };
}

// Embed one after another from the starting index — an embed is one index long, so the cursor advances
// per image and the batch keeps the order the user picked/dropped them in.
function insertImages(index: number, urls: (string | null)[]) {
  let cursor = index;

  urls.forEach((url) => {
    if (!url) return;

    quill.insertEmbed(cursor, 'image', url, 'user');
    cursor += 1;
  });

  quill.setSelection(cursor, 0, 'silent');
}

// Keep base64 <img> out of the editor content: strip data: images that arrive via pasted HTML so the
// document never carries inline binary (which is what exceeded Mongo's 16MB document cap). Already
// hosted (http) images pass through untouched. Real user pastes only — `applyHtmlToEditor` seeds skip
// the matcher (`isApplyingStoredHtml`), otherwise opening a legacy answer with an inline base64 image
// would strip it from the editor and the first keystroke would persist the loss.
function registerBase64Guard() {
  const DeltaClass: any = Quill.import('delta');

  quill.clipboard.addMatcher('IMG', (node, delta) => {
    if (isApplyingStoredHtml) return delta;

    const source = (node as Element).getAttribute('src') || '';

    return source.startsWith('data:') ? new DeltaClass() : delta;
  });
}

// Medical-template pre-fills arrive as plain text (authored in the legacy settings textarea).
// Fed to Quill as HTML, newlines collapse to spaces and `weight <10 lbs` loses everything after
// the `<` as a bogus tag — so escape it and make the line breaks real before pasting.
function toEditorHtml(value: string): string {
  if (!value || looksLikeHtml(value)) return value;

  const escaped = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return escaped.replace(/\r\n|\r|\n/g, '<br>');
}

function getCurrentEditorHtml(): string {
  if (!quill) return _model.value;

  const QUILL_DEFAULT_EMPTY_VALUE = '<p><br></p>';
  const html = quill.root.innerHTML;

  return html === QUILL_DEFAULT_EMPTY_VALUE ? '' : html;
}

function updateModel() {
  const quillDefaulEmptyValue = '<p><br></p>';
  let newValue = quill.root.innerHTML;

  if (newValue === quillDefaulEmptyValue) newValue = '';

  _model.value = newValue;
}

function syncModelFromQuill() {
  const html = getCurrentEditorHtml();

  _model.value = html;
  // Flush immediately for template apply — a debounced model was dropping HTML on quick Save.
  emits('update:modelValue', html);
}

// `shouldSyncModel` is false for the initial load only. Syncing there emitted `update:modelValue`
// before the user touched anything, so opening an exam marked the field changed and a Save with no
// edit rewrote the stored answer with Quill's re-normalised HTML. Leave the model on the saved value
// and let the first real `text-change` update it.
function applyHtmlToEditor(html: string, shouldSyncModel: boolean = true) {
  const normalizedHtml = normalizeHtmlForQuill(html);

  if (!quill) {
    if (!shouldSyncModel) return;

    _model.value = normalizedHtml;
    emits('update:modelValue', normalizedHtml);

    return;
  }

  // Quill's clipboard converts HTML to a Delta and keeps only registered formats — that conversion
  // is what stops template markup reaching the DOM unsanitised. Never fall back to
  // `quill.root.innerHTML` here: it bypasses the conversion, and `syncModelFromQuill` would then
  // persist the raw markup into the exam answer, the profile view (`v-html`) and the printed PDF.
  // The old fallback also misfired on valid input — it tested `quill.getText()`, which does not
  // count embeds, so an image-only body looked "empty" even after a successful paste.
  isApplyingStoredHtml = true;

  try {
    quill.clipboard.dangerouslyPasteHTML(normalizedHtml, 'silent');
  } finally {
    isApplyingStoredHtml = false;
  }

  // Bail out rather than write an empty value over a body that had content — markup the conversion
  // cannot represent comes back empty, and `syncModelFromQuill` below would persist that emptiness.
  // `getLength()` counts embeds (an image is 1), unlike the `getText()` the old fallback used.
  const convertedToNothing = quill.getLength() <= 1 && !!normalizedHtml;

  if (convertedToNothing || !shouldSyncModel) return;

  syncModelFromQuill();
}

// EVENTS
function initQuill() {
  if (!quillEditor.value || !Quill) return;

  const modules: any = { toolbar: buildToolbar() };

  const shouldEnableUpload = !hideImage.value && uploadImage.value;

  if (!hideImage.value) modules.ImageResize = {};

  if (shouldEnableUpload) modules.uploader = { mimetypes: UPLOAD_MIMETYPES, handler: onEditorImageDrop };

  quill = new Quill(quillEditor.value, {
    modules,
    theme: 'snow',
    placeholder: props.placeholder,
  });

  quill.on('text-change', updateModel);
  quill.on('selection-change', onEditorSelectionChange);

  if (uploadImage.value) registerBase64Guard();

  if (!_model.value) return;

  applyHtmlToEditor(_model.value, false);
}

// Typing only reaches the parent through the 400ms debounce, so clicking Save right after typing
// submitted the previous value and the new text was lost. Leaving the editor flushes it now.
function onEditorSelectionChange(range: unknown) {
  if (range) return;

  syncModelFromQuill();
}

function onModelDebounce() {
  emits('update:modelValue', _model.value);
}

// The parent form can write the field's model AFTER mount — picking a surgery / medication /
// procedure template copies the medical template's notes into `field.model`. `_model` is only read
// at setup and in `initQuill`, so without this the pre-fill never reached Quill: the editor stayed
// blank while the model silently held (or, on blur, wiped) the template text. Echoes of our own
// emits are skipped so typing never round-trips back into the editor and moves the cursor.
function onExternalModelValue(newValue?: string) {
  const incoming = newValue ?? '';

  const isOwnEmitEcho = incoming === _model.value || incoming === getCurrentEditorHtml();

  if (isOwnEmitEcho) return;

  applyHtmlToEditor(toEditorHtml(incoming));
}

function onToggleTemplateMenu(event: Event) {
  templateMenu.value?.toggle(event);
}

function resolveTemplateBody(template: NoteTemplateOption): Promise<string> {
  const resolver = props.field?.settings?.resolveNoteTemplateBody;

  if (typeof resolver === 'function') return resolver(template);

  if (typeof template.body === 'string') return Promise.resolve(template.body);

  return Promise.resolve('');
}

function applyResolvedNoteTemplate(template: NoteTemplateOption) {
  resolveTemplateBody(template)
    .then((html) => applyHtmlToEditor(html))
    .catch(() => applyHtmlToEditor(''));
}

function onSelectNoteTemplate(template: NoteTemplateOption) {
  if (!isRichTextEmpty(getCurrentEditorHtml())) {
    pendingNoteTemplate.value = template;
    replaceTemplateConfirmVisible.value = true;
    return;
  }

  applyResolvedNoteTemplate(template);
}

function onConfirmReplaceTemplate() {
  if (!pendingNoteTemplate.value) return;

  applyResolvedNoteTemplate(pendingNoteTemplate.value);
  pendingNoteTemplate.value = null;
}

function onCancelReplaceTemplate() {
  pendingNoteTemplate.value = null;
}

// Quill routes both drops and screenshot pastes of image files through the uploader module handler, and
// hands over every image in the drop at once.
function onEditorImageDrop(range: any, files: File[]) {
  const index = range ? range.index : quill.getLength();

  uploadAndInsert(files, index);
}

function onImageButtonClick() {
  const fileInput = document.createElement('input');
  fileInput.setAttribute('type', 'file');
  fileInput.setAttribute('accept', 'image/*');
  fileInput.setAttribute('multiple', 'multiple');

  fileInput.onchange = () => {
    const files = Array.from(fileInput.files || []);
    if (!files.length) return;

    const selection = quill.getSelection(true);
    uploadAndInsert(files, selection ? selection.index : quill.getLength());
  };

  fileInput.click();
}

// A batch uploads in parallel but is embedded only once every upload settles: inserting as each promise
// resolves would drop them all at the same index in whatever order the network returned. A failed file
// resolves to null (the handler already toasts) so one bad image doesn't lose the rest of the batch.
function uploadAndInsert(files: File[], index: number) {
  const upload = uploadImage.value;

  if (!upload || !files.length) return;

  uploading.value = true;
  uploadCount.value = files.length;

  const UPLOADS = files.map((file) => upload(file).catch(() => null));

  Promise.all(UPLOADS)
    .then((urls) => insertImages(index, urls))
    .finally(() => {
      uploading.value = false;
      uploadCount.value = 0;
    });
}
</script>

<template>
  <div
    :class="[
      canUseNoteTemplates ? 'contents' : 'editor',
      !canUseNoteTemplates && field.state.disabled ? 'pointer-events-none opacity-50' : '',
    ]"
  >
    <div
      v-if="canPickNoteTemplate"
      class="col-start-2 row-start-1 flex items-center self-center"
    >
      <Button
        v-tooltip.top="{ value: $t('components.notes.template'), showDelay: 400 }"
        text
        aria-haspopup="true"
        class="h-6 w-6 shrink-0 rounded-md p-0 text-slate-500 hover:bg-gray-100 hover:text-slate-700"
        icon="pi pi-file-edit"
        severity="secondary"
        type="button"
        :aria-controls="templateMenuDomId"
        @click="onToggleTemplateMenu"
      />
    </div>

    <div
      :class="[
        'editor',
        canUseNoteTemplates ? 'col-span-2 col-start-1 row-start-2' : '',
        field.state.disabled ? 'pointer-events-none opacity-50' : '',
      ]"
    >
      <Menu
        v-if="canPickNoteTemplate"
        popup
        :id="templateMenuDomId"
        class="min-w-40"
        pt:menu="text-sm"
        ref="templateMenu"
        :model="templateMenuItems"
      />

      <MNoteTemplateReplaceConfirm
        v-if="canPickNoteTemplate"
        v-model:visible="replaceTemplateConfirmVisible"
        @onCancel="onCancelReplaceTemplate"
        @onConfirm="onConfirmReplaceTemplate"
      />

      <div
        class="field-editor mt-2 w-full rounded rounded-t-none"
        ref="quillEditor"
      />

      <div
        v-if="uploading"
        class="mt-1 flex items-center gap-2 text-xs italic text-slate-400"
      >
        <i class="pi pi-spin pi-spinner"></i>

        <span>{{ $t('common.loading') }}{{ isBatchUpload ? ` (${uploadCount})` : '' }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.field-editor {
  border: v-bind(editorBorder);
  min-height: v-bind(editorHeight);
}
</style>

<style>
.editor .ql-toolbar.ql-snow {
  @apply rounded rounded-b-none;
  border: v-bind(editorBorder);
}
</style>
